/**
 * Hook personalizado para gestionar terapeutas desde Supabase
 * Integra datos reales con el formato existente del frontend
 */

import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase/client';
import { useSupabaseError } from './useSupabase';
import { TherapistExtended, Professional } from '../types';

// Tipos para los datos de la base de datos
export interface TerapeutaDB {
  id: string;
  slug: string;
  nombre: string;
  apellido: string;
  nombre_completo: string;
  titulo: string;
  descripcion: string;
  descripcion_extendida?: string;
  numero_licencia?: string;
  registro_profesional?: string;
  especialidades: string[];
  enfoque_terapeutico?: string[];
  poblacion_objetivo?: string[];
  anos_experiencia: number;
  imagen_perfil: string;
  imagen_pequena?: string;
  galeria_imagenes?: any;
  email?: string;
  telefono?: string;
  whatsapp?: string;
  instagram?: string;
  tiktok?: string;
  facebook?: string;
  linkedin?: string;
  educacion?: any;
  certificaciones?: any;
  idiomas?: string[];
  filosofia_personal?: string;
  hobbies?: string[];
  servicios: any;
  horarios_atencion?: any;
  modalidades: string[];
  testimonios?: any;
  logros?: any;
  activo: boolean;
  visible_web: boolean;
  orden_display: number;
  meta_titulo?: string;
  meta_descripcion?: string;
  palabras_clave?: string[];
  url_canonica?: string;
  imagen_og?: string;
  created_at: string;
  updated_at: string;
}

interface UseTerapeutasReturn {
  terapeutas: Professional[];
  terapeutasExtended: TherapistExtended[];
  loading: boolean;
  error: string | null;
  refreshTerapeutas: () => Promise<void>;
  getTerapeutaBySlug: (slug: string) => TherapistExtended | undefined;
  getTerapeutasBySpecialty: (specialty: string) => Professional[];
}

// Formatea un monto CLP en el mismo formato usado en el resto del sistema ("$36.000").
// Si el valor no viene o no es un número válido, usa el fallback de sesión básica individual.
const formatTarifa = (value?: number | null): string => {
  const amount = typeof value === 'number' && !Number.isNaN(value) && value > 0 ? value : 36000;
  return `$${amount.toLocaleString('es-CL')}`;
};

// Función para convertir datos de DB a formato Professional
const dbToProfessional = (terapeuta: any): Professional => ({
  id: terapeuta.slug || `${terapeuta.nombre}-${terapeuta.apellido}`.toLowerCase().replace(/\s+/g, '-'),
  name: terapeuta.nombre_completo || `${terapeuta.nombre} ${terapeuta.apellido}`,
  title: terapeuta.titulo,
  description: terapeuta.descripcion || terapeuta.biografia || 'Profesional de la salud mental',
  image: terapeuta.imagen_perfil || terapeuta.foto_url || '/assets/img/placeholder-therapist.webp',
  imageSmall: terapeuta.imagen_pequena || terapeuta.imagen_perfil || terapeuta.foto_url || '/assets/img/placeholder-therapist.webp',
  specialties: terapeuta.especialidades || [],
  email: terapeuta.email,
  bookingUrl: `/agendar?therapistId=${terapeuta.slug || terapeuta.id}`
});

// Función para convertir datos de DB a formato TherapistExtended
const dbToTherapistExtended = (terapeuta: any): TherapistExtended => {
  const professional = dbToProfessional(terapeuta);
  
  return {
    ...professional,
    personalInfo: {
      education: terapeuta.educacion || [],
      certifications: terapeuta.certificaciones || [],
      languages: terapeuta.idiomas || ['Español'],
      hobbies: terapeuta.hobbies || [],
      personalPhilosophy: terapeuta.filosofia_personal
    },
    professionalInfo: {
      licenseNumber: terapeuta.numero_licencia,
      registroSuperintendencia: terapeuta.registro_profesional,
      experience: [],
      approach: terapeuta.descripcion_extendida || terapeuta.descripcion,
      philosophy: terapeuta.filosofia_personal || '',
      methodology: terapeuta.enfoque_terapeutico || [],
      achievements: terapeuta.logros || [],
      specialAreas: terapeuta.especialidades || [],
      clientTypes: terapeuta.poblacion_objetivo || []
    },
    socialMedia: {
      instagram: terapeuta.instagram,
      tiktok: terapeuta.tiktok,
      facebook: terapeuta.facebook,
      whatsapp: terapeuta.whatsapp,
      linkedin: terapeuta.linkedin
    },
    media: {
      profileImage: terapeuta.imagen_perfil,
      gallery: terapeuta.galeria_imagenes || [],
      testimonials: terapeuta.testimonios || []
    },
    services: terapeuta.servicios || [],
    availability: {
      schedule: terapeuta.horarios_atencion || {},
      modalidades: (terapeuta.modalidades || ['presencial']) as ('presencial' | 'online' | 'hibrido')[],
      prices: {
        individual: {
          presencial: formatTarifa(terapeuta.tarifa_particular),
          online: formatTarifa(terapeuta.tarifa_particular)
        }
      },
      sessionDuration: '50 minutos',
      emergencyContact: true
    },
    seo: {
      metaTitle: terapeuta.meta_titulo || `${terapeuta.nombre_completo} | Conectemos Chile`,
      metaDescription: terapeuta.meta_descripcion || terapeuta.descripcion,
      keywords: terapeuta.palabras_clave || [],
      canonicalUrl: terapeuta.url_canonica || `https://www.conectemoschile.cl/terapista/${terapeuta.slug}`,
      ogImage: terapeuta.imagen_og
    },
    pageConfig: {
      showGallery: true,
      showTestimonials: (terapeuta.testimonios?.length || 0) > 0,
      showSchedule: true,
      customSections: ['education', 'methodology', 'achievements']
    }
  };
};

/**
 * Hook principal para gestionar terapeutas
 */
export const useTerapeutas = (): UseTerapeutasReturn => {
  const [terapeutas, setTerapeutas] = useState<Professional[]>([]);
  const [terapeutasExtended, setTerapeutasExtended] = useState<TherapistExtended[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { handleError } = useSupabaseError();

  const loadTerapeutas = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('🔄 Cargando terapeutas desde Supabase...');
      
      // SEC-040: lista explícita en vez de `select('*')` — `terapeutas` tiene
      // allowlist de columnas para `anon` (sec040b); estas son las que
      // consumen dbToProfessional / dbToTherapistExtended (todas públicas).
      // `email` lo mapea el mapper pero NINGÚN componente público lo renderiza
      // → fuera de la lista. `registro_profesional` / `numero_licencia` SÍ se
      // exponen desde FUNC-113 (N° de registro Superintendencia de Salud /
      // Colegio de Psicólogos — dato público, RNPI) — requiere el GRANT de
      // `sql/migrations/func113_terapeutas_registro_allowlist_2026_09_11.sql`.
      const { data, error: supabaseError } = await supabase
        .from('terapeutas')
        .select(`
          id, slug, nombre, apellido, nombre_completo, titulo,
          descripcion, biografia, filosofia_personal,
          especialidades, enfoque_terapeutico, poblacion_objetivo,
          anos_experiencia, educacion, certificaciones, idiomas, hobbies, logros,
          servicios, modalidades, horarios_atencion, tarifa_particular,
          foto_url, imagen_perfil, imagen_pequena, imagen_og, galeria_imagenes,
          testimonios, meta_titulo, meta_descripcion, palabras_clave, url_canonica,
          instagram, tiktok, facebook, whatsapp, linkedin,
          registro_profesional, numero_licencia,
          activo, visible_web, orden_display
        `)
        .eq('activo', true)
        .eq('visible_web', true)
        .order('orden_display', { ascending: true });

      if (supabaseError) {
        console.error('🚨 Error de Supabase:', supabaseError);
        setError('Error al cargar terapeutas');
        setTerapeutas([]);
        setTerapeutasExtended([]);
        return;
      }

      if (data) {
        console.log('✅ Terapeutas cargados:', data.length, 'registros');
        
        // Convertir a formato Professional para compatibilidad
        const professionalsData = data.map(dbToProfessional);
        setTerapeutas(professionalsData);

        // Convertir a formato TherapistExtended
        const extendedData = data.map(dbToTherapistExtended);
        setTerapeutasExtended(extendedData);
      }
    } catch (err: any) {
      console.error('❌ Error cargando terapeutas:', err);
      setError(err.message || 'Error al cargar terapeutas');
      setTerapeutas([]);
      setTerapeutasExtended([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshTerapeutas = async () => {
    await loadTerapeutas();
  };

  const getTerapeutaBySlug = useCallback((slug: string): TherapistExtended | undefined => {
    return terapeutasExtended.find(terapeuta => terapeuta.id === slug);
  }, [terapeutasExtended]);

  const getTerapeutasBySpecialty = useCallback((specialty: string): Professional[] => {
    return terapeutas.filter(terapeuta => 
      terapeuta.specialties.some(spec => 
        spec.toLowerCase().includes(specialty.toLowerCase())
      )
    );
  }, [terapeutas]);

  // Cargar terapeutas al montar el componente
  useEffect(() => {
    loadTerapeutas();
  }, [loadTerapeutas]);

  return {
    terapeutas,
    terapeutasExtended,
    loading,
    error,
    refreshTerapeutas,
    getTerapeutaBySlug,
    getTerapeutasBySpecialty
  };
};

// Hook especializado solo para obtener terapeutas básicos (más rápido)
export const useProfessionals = () => {
  const [professionals, setProfessionals] = useState<Professional[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { handleError } = useSupabaseError();

  const fetchProfessionals = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('🔄 useProfessionals: Cargando profesionales desde Supabase...');
      
      const { data, error: supabaseError } = await supabase
        .from('terapeutas')
        .select('slug, nombre_completo, titulo, descripcion, imagen_perfil, imagen_pequena, especialidades, biografia, foto_url, nombre, apellido')
        .eq('activo', true)
        .eq('visible_web', true)
        .order('orden_display', { ascending: true });

      if (supabaseError) {
        console.error('🚨 useProfessionals error:', supabaseError);
        setError('Error al cargar profesionales');
        setProfessionals([]);
        return;
      }

      if (data) {
        console.log('✅ useProfessionals datos encontrados:', data.length, 'registros');
        const formattedData: Professional[] = data.map(item => ({
          id: item.slug,
          name: item.nombre_completo,
          title: item.titulo,
          description: item.descripcion,
          image: item.imagen_perfil,
          imageSmall: item.imagen_pequena || item.imagen_perfil,
          specialties: item.especialidades || [],
          bookingUrl: `/agendar?therapistId=${item.slug}`
        }));

        setProfessionals(formattedData);
      }
    } catch (err: any) {
      console.error('❌ Error cargando profesionales:', err);
      setError(err.message || 'Error al cargar profesionales');
      setProfessionals([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProfessionals();
  }, [fetchProfessionals]);

  return {
    professionals,
    loading,
    error,
    refetch: fetchProfessionals
  };
};

export default useTerapeutas;